import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import Header from '../components/header/Header.jsx';
import Footer from '../components/footer/Footer.jsx';
import { useAuth } from '../contexts/AuthContext';
import { getTeam, createInvite, revokeInvite } from '../services/apiService';

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');

const Team = () => {
    const { isAuthenticated, loading: authLoading } = useAuth();
    const [team, setTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [newInvite, setNewInvite] = useState(null);
    const [copied, setCopied] = useState(false);

    const loadTeam = useCallback(async () => {
        try {
            setTeam(await getTeam());
            setError('');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) loadTeam();
    }, [isAuthenticated, loadTeam]);

    const handleInvite = async (e) => {
        e.preventDefault();
        setSending(true);
        setError('');
        setNewInvite(null);
        setCopied(false);
        try {
            setNewInvite(await createInvite(inviteEmail));
            setInviteEmail('');
            await loadTeam();
        } catch (err) {
            setError(err.message);
        } finally {
            setSending(false);
        }
    };

    const handleRevoke = async (invite) => {
        if (!window.confirm(`Cancel the invite for ${invite.email}?`)) return;
        try {
            await revokeInvite(invite.inviteId);
            if (newInvite?.inviteId === invite.inviteId) setNewInvite(null);
            await loadTeam();
        } catch (err) {
            setError(err.message);
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(newInvite.inviteUrl);
            setCopied(true);
        } catch {
            setCopied(false);
        }
    };

    if (!authLoading && !isAuthenticated) return <Navigate to="/signin" replace />;

    const isCompanyAdmin = team?.role === 'admin';
    const seatsUsed = team ? team.members.length + (team.invites?.length || 0) : 0;

    return (
        <div className='min-h-screen'>
            <Header />
            <main className="mx-auto max-w-screen-lg p-4">
                <h1 className="mb-1 text-2xl font-bold">Team{team?.companyName ? ` — ${team.companyName}` : ''}</h1>
                {team && (
                    <p className="mb-6 text-sm text-gray-600">
                        {team.maxUsers != null
                            ? `${seatsUsed} of ${team.maxUsers} seats used${isCompanyAdmin ? ' (including pending invites)' : ''}`
                            : `${team.members.length} members`}
                    </p>
                )}

                {error && <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">{error}</div>}
                {(loading || authLoading) && !team && <p>Loading team...</p>}

                {team && isCompanyAdmin && (
                    <section className="mb-8 rounded-lg bg-gray-100 p-4 shadow-md">
                        <h2 className="mb-3 text-lg font-bold">Invite a teammate</h2>
                        <form onSubmit={handleInvite} className="flex flex-col gap-2 sm:flex-row">
                            <input
                                type="email"
                                required
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="teammate@company.com"
                                className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
                            />
                            <button
                                type="submit"
                                disabled={sending}
                                className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                                {sending ? 'Creating...' : 'Create invite link'}
                            </button>
                        </form>

                        {newInvite && (
                            <div className="mt-4 rounded border border-green-300 bg-green-50 p-3 text-sm">
                                <p className="mb-2">
                                    Send this link to <strong>{newInvite.email}</strong>. It works once, only for that address,
                                    and expires on {formatDate(newInvite.expiresAt)}. It won't be shown again.
                                </p>
                                <div className="flex flex-col gap-2 sm:flex-row">
                                    <input readOnly value={newInvite.inviteUrl} onFocus={(e) => e.target.select()} className="flex-1 rounded border border-gray-300 bg-white px-2 py-1 font-mono text-xs" />
                                    <button type="button" onClick={copyLink} className="rounded bg-green-600 px-3 py-1 text-white hover:bg-green-700">
                                        {copied ? 'Copied' : 'Copy link'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {team && (
                    <section className="mb-8">
                        <h2 className="mb-3 text-lg font-bold">Members</h2>
                        <div className="overflow-x-auto rounded-lg bg-gray-100 p-4 shadow-md">
                            <table className="min-w-full bg-white text-left text-sm">
                                <thead>
                                    <tr>
                                        <th className="border-b-2 border-gray-300 px-4 py-2 text-blue-500">Name</th>
                                        <th className="border-b-2 border-gray-300 px-4 py-2 text-blue-500">Email</th>
                                        <th className="border-b-2 border-gray-300 px-4 py-2 text-blue-500">Role</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {team.members.map((member) => (
                                        <tr key={member.email}>
                                            <td className="border-b border-gray-200 px-4 py-2">{member.fullName}</td>
                                            <td className="border-b border-gray-200 px-4 py-2">{member.email}</td>
                                            <td className="border-b border-gray-200 px-4 py-2 capitalize">{member.role}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

                {team && isCompanyAdmin && (
                    <section className="mb-8">
                        <h2 className="mb-3 text-lg font-bold">Pending invites</h2>
                        {team.invites.length === 0 ? (
                            <p className="text-sm text-gray-600">No pending invites.</p>
                        ) : (
                            <div className="overflow-x-auto rounded-lg bg-gray-100 p-4 shadow-md">
                                <table className="min-w-full bg-white text-left text-sm">
                                    <thead>
                                        <tr>
                                            <th className="border-b-2 border-gray-300 px-4 py-2 text-blue-500">Email</th>
                                            <th className="border-b-2 border-gray-300 px-4 py-2 text-blue-500">Expires</th>
                                            <th className="border-b-2 border-gray-300 px-4 py-2" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {team.invites.map((invite) => (
                                            <tr key={invite.inviteId}>
                                                <td className="border-b border-gray-200 px-4 py-2">{invite.email}</td>
                                                <td className="border-b border-gray-200 px-4 py-2">{formatDate(invite.expiresAt)}</td>
                                                <td className="border-b border-gray-200 px-4 py-2 text-right">
                                                    <button type="button" onClick={() => handleRevoke(invite)} className="text-red-600 hover:underline">
                                                        Revoke
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                )}
            </main>
            <Footer />
        </div>
    );
};

export default Team;
